import React, { useEffect, useMemo } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { Field, useFormikContext } from "formik";

import FormSection from "../FormSection";
import RentalOrderHistoryPanel from "../RentalOrderHistoryPanel";

import { useFormat } from "../../../../components/FormatContext";
import { fromCents } from "../../utils/money";

/* =========================
   Helpers: timestamp without tz
========================= */

const pad2 = (n) => String(n).padStart(2, "0");

// Date -> input[type=datetime-local] value: YYYY-MM-DDTHH:mm
function toDatetimeLocalValue(d) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
        d.getHours()
    )}:${pad2(d.getMinutes())}`;
}

// datetime-local value (YYYY-MM-DDTHH:mm) -> timestamp-no-tz string (YYYY-MM-DDTHH:mm:ss)
function toTimestampNoTZString(localValue) {
    if (!localValue) return "";
    return localValue.length === 16 ? `${localValue}:00` : localValue;
}

// parse "YYYY-MM-DDTHH:mm:ss" (no Z) as local Date
function parseNoTZ(s) {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

// days between dates by date-only (refund policy usually day-based)
function daysBeforeStart(start, cancelled) {
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const c = new Date(cancelled.getFullYear(), cancelled.getMonth(), cancelled.getDate()).getTime();
    return Math.floor((s - c) / (24 * 60 * 60 * 1000));
}

function getRefundPercentByDays(days, detail) {
    if (!detail) return 0;

    const p7 = Number(detail.cancellation7DaysRefund ?? 0);
    const p15 = Number(detail.cancellation15DaysRefund ?? 0);
    const p30 = Number(detail.cancellation30DaysRefund ?? 0);
    const p60 = Number(detail.cancellation60DaysRefund ?? 0);

    if (days >= 60) return p60;
    if (days >= 30) return p30;
    if (days >= 15) return p15;
    if (days >= 7) return p7;
    return 0;
}

const StatusTab = ({ rentalOrderHistory }) => {
    const { setFieldValue, values } = useFormikContext();
    const { formatCurrency } = useFormat();

    const status = values?.rentalOrder?.status;

    // supplier cancellation config
    const supplierDetail = useMemo(() => {
        return (
            values?.rentalOrder?.Shop?.Supplier?.SupplierRentalDetail ||
            values?.relations?.shop?.Supplier?.SupplierRentalDetail ||
            null
        );
    }, [values]);

    const startDate = useMemo(() => parseNoTZ(values?.rentalOrder?.startDate), [values?.rentalOrder?.startDate]);
    const cancelledAt = useMemo(
        () => parseNoTZ(values?.rentalOrderCancellation?.cancelledAt),
        [values?.rentalOrderCancellation?.cancelledAt]
    );

    const calc = values?.pricingSnapshot?.calculation || {};

    const totalPriceCents = Number(calc.totalPriceCents ?? 0);
    const managementFeeTotalCents = Number(calc.managementFeeTotalCents ?? 0);
    const paidAmountCents = Number(calc.paidAmountCents ?? 0);

    const commissionTotalCents = Number(calc.commissionTotalCents ?? 0);
    const insurancesTotalCents = Number(calc.insurancesTotalCents ?? 0);
    const rmbDiscountCents = Number(calc.rmbDiscountCents ?? 0);

    const currency = values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR";

    // allowed status -> emails switches
    useEffect(() => {
        const allowed = ["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED"];
        if (!allowed.includes(status)) {
            setFieldValue("emailSettings", { sendToCustomer: false, sendToSupplier: false });
        }
    }, [status, setFieldValue]);

    // when entering CANCELLED: set defaults (user-friendly)
    useEffect(() => {
        if (status !== "CANCELLED") return;

        if (!values?.rentalOrderCancellation?.cancelBy) {
            setFieldValue("rentalOrderCancellation.cancelBy", "RENTAL");
        }
        if (!values?.rentalOrderCancellation?.cancelledAt) {
            setFieldValue("rentalOrderCancellation.cancelledAt", toTimestampNoTZString(toDatetimeLocalValue(new Date())));
        }
    }, [status, setFieldValue]); // intentionally not depending on values to avoid loops

    // auto-calc percent & refund cents
    useEffect(() => {
        if (status !== "CANCELLED") return;
        if (!startDate || !cancelledAt) return;

        const days = daysBeforeStart(startDate, cancelledAt);
        const percent = getRefundPercentByDays(days, supplierDetail); // 0..100

        const baseCents = Math.max(0, totalPriceCents - managementFeeTotalCents - rmbDiscountCents);

        // ✅ 理应退款
        const shouldRefundCents =
            percent === 0
                ? Math.max(0, commissionTotalCents + managementFeeTotalCents + insurancesTotalCents - rmbDiscountCents)
                : Math.round((baseCents * percent) / 100);

        // ✅ 实际退款（不超过已支付）
        const actualRefundCents = Math.min(shouldRefundCents, Math.max(0, paidAmountCents));

        setFieldValue("rentalOrderCancellation.cancelPercentage", percent);
        setFieldValue("rentalOrderCancellation.refundAmountCents", actualRefundCents);
    }, [
        status,
        startDate?.getTime(),
        cancelledAt?.getTime(),
        supplierDetail,
        totalPriceCents,
        managementFeeTotalCents,
        paidAmountCents,
        commissionTotalCents,
        insurancesTotalCents,
        setFieldValue,
    ]);

    const preview = useMemo(() => {
        if (status !== "CANCELLED") return null;
        if (!startDate || !cancelledAt) return { ready: false };

        const days = daysBeforeStart(startDate, cancelledAt);
        const percent = Number(values?.rentalOrderCancellation?.cancelPercentage ?? 0);

        const baseCents = Math.max(0, totalPriceCents - managementFeeTotalCents);

        const shouldRefundCents =
            percent === 0
                ? Math.max(0, commissionTotalCents + managementFeeTotalCents + insurancesTotalCents - rmbDiscountCents)
                : Math.round((baseCents * percent) / 100);

        const actualRefundCents = Math.min(shouldRefundCents, Math.max(0, paidAmountCents));

        return {
            ready: true,
            days,
            percent,
            baseCents,
            shouldRefundCents,
            actualRefundCents,
            capped: actualRefundCents < shouldRefundCents,
        };
    }, [
        status,
        startDate,
        cancelledAt,
        values?.rentalOrderCancellation?.cancelPercentage,
        totalPriceCents,
        managementFeeTotalCents,
        paidAmountCents,
        commissionTotalCents,
        insurancesTotalCents,
    ]);

    return (
        <FormSection title="Status & History">
            <Row>
                <Col md={6}>
                    <Field name="rentalOrder.status">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Booking Status</Form.Label>
                                <Form.Select {...field} isInvalid={meta.touched && !!meta.error}>
                                    <option value="UNPAID">Unpaid</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="CONFIRMED">Confirmed</option>
                                    <option value="COMPLETED">Completed</option>
                                    <option value="CANCELLED">Cancelled</option>
                                    <option value="INVOICED">Invoiced</option>
                                    <option value="DISPUTED">Disputed</option>
                                </Form.Select>
                            </Form.Group>
                        )}
                    </Field>

                    {/* CANCELLED: who + when + auto refund */}
                    {status === "CANCELLED" && (
                        <>
                            <Field name="rentalOrderCancellation.cancelBy">
                                {({ field }) => (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Cancelled by</Form.Label>
                                        <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>
                                            Select who is responsible for the cancellation.
                                        </div>
                                        <div>
                                            <Form.Check
                                                type="radio"
                                                inline
                                                label="Rental"
                                                name={field.name}
                                                value="RENTAL"
                                                checked={field.value === "RENTAL"}
                                                onChange={() => setFieldValue(field.name, "RENTAL")}
                                            />
                                            <Form.Check
                                                type="radio"
                                                inline
                                                label="Supplier"
                                                name={field.name}
                                                value="SUPPLIER"
                                                checked={field.value === "SUPPLIER"}
                                                onChange={() => setFieldValue(field.name, "SUPPLIER")}
                                            />
                                            <Form.Check
                                                type="radio"
                                                inline
                                                label="Customer"
                                                name={field.name}
                                                value="CUSTOMER"
                                                checked={field.value === "CUSTOMER"}
                                                onChange={() => setFieldValue(field.name, "CUSTOMER")}
                                            />
                                        </div>
                                    </Form.Group>
                                )}
                            </Field>

                            <Form.Group className="mb-3">
                                <Form.Label>Cancelled at</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    value={cancelledAt ? toDatetimeLocalValue(cancelledAt) : ""}
                                    onChange={(e) =>
                                        setFieldValue(
                                            "rentalOrderCancellation.cancelledAt",
                                            toTimestampNoTZString(e.target.value)
                                        )
                                    }
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Refund preview</Form.Label>

                                {!preview || !preview.ready ? (
                                    <div style={{ fontSize: 14, opacity: 0.7 }}>
                                        Please select cancellation time to calculate refund.
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 14 }}>
                                        <div>
                                            Days before start: <b>{preview.days}</b>{" "}
                                            {preview.days < 0 ? (
                                                <span style={{ color: "#b45309" }}>(after start date)</span>
                                            ) : null}
                                        </div>

                                        <div style={{ marginTop: 6 }}>
                                            Supplier refund percent: <b>{preview.percent}%</b>
                                        </div>

                                        <div style={{ marginTop: 6 }}>
                                            Refund amount:{" "}
                                            <b>{formatCurrency(fromCents(preview.actualRefundCents), currency)}</b>{" "}
                                            <span style={{ opacity: 0.65 }}>
                                                (should: {formatCurrency(fromCents(preview.shouldRefundCents), currency)})
                                            </span>
                                        </div>

                                        {preview.capped ? (
                                            <div style={{ marginTop: 6, color: "#b91c1c" }}>
                                                Capped by paid amount: {formatCurrency(fromCents(paidAmountCents), currency)}
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 6, opacity: 0.75 }}>
                                                Paid amount: {formatCurrency(fromCents(paidAmountCents), currency)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Form.Group>
                        </>
                    )}

                    <Field name="comment">
                        {({ field, meta }) => (
                            <Form.Group className="mb-3">
                                <Form.Label>Additional Comments</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    {...field}
                                    placeholder="Enter any additional comments..."
                                    isInvalid={meta.touched && !!meta.error}
                                />
                                <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </Field>

                    {["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED"].includes(status) && (
                        <>
                            <Field name="emailSettings.sendToCustomer">
                                {({ field }) => (
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="switch"
                                            label="Send email to customers"
                                            checked={field.value || false}
                                            onChange={(e) => setFieldValue(field.name, e.target.checked)}
                                        />
                                    </Form.Group>
                                )}
                            </Field>

                            <Field name="emailSettings.sendToSupplier">
                                {({ field }) => (
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="switch"
                                            label="Send email to supplier"
                                            checked={field.value || false}
                                            onChange={(e) => setFieldValue(field.name, e.target.checked)}
                                        />
                                    </Form.Group>
                                )}
                            </Field>
                        </>
                    )}
                </Col>

                <Col md={6} className="mb-4">
                    <RentalOrderHistoryPanel rentalOrderHistory={rentalOrderHistory} />
                </Col>
            </Row>
        </FormSection>
    );
};

export default StatusTab;